<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4"
    style="z-index: 2000"
    role="dialog"
    aria-modal="true"
    aria-labelledby="force-password-title"
  >
    <!-- No close button and no outside-click close. After the deadline the customer must update the
         password; before it the only way past is "continue without a new password" and its warning. -->
    <form
      class="bg-white rounded shadow-modal w-full max-w-[600px] max-h-full overflow-y-auto flex flex-col"
      dir="rtl"
      novalidate
      @submit.prevent="submit"
    >
      <div
        class="flex flex-col items-center gap-[12px] text-center px-4 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8"
      >
        <div
          class="flex justify-center items-center h-[44px] w-[44px] flex-none rounded-full bg-complementary-600 text-complementary-900"
        >
          <span class="material-symbols-rounded" aria-hidden="true">verified_user</span>
        </div>
        <h2
          id="force-password-title"
          class="text-xl md:text-2xl font-bold text-primary-900"
        >
          {{ enforced ? "נדרש עדכון סיסמה" : "עדכון אבטחה תקופתי" }}
        </h2>
        <p v-if="enforced" class="text-sm md:text-base text-grays-900">
          הוספנו שכבת הגנה חדשה למערכת, יש לבחור סיסמה חדשה וחזקה.
          <br class="hidden md:inline" />
          מומלץ להשתמש בסיסמה שלא משמשת אתכם באתרים אחרים.
        </p>
        <p v-else class="text-sm md:text-base text-grays-900">
          החל מ-<bdi class="font-bold">{{ deadlineText }}</bdi> כניסה לאתר תתאפשר עם
          סיסמה חדשה בלבד.
          <br class="hidden md:inline" />
          מומלץ לעדכן אותה כעת כדי למנוע חסימות בגישה לאתר.
        </p>

        <div class="flex flex-col gap-[8px] w-full md:w-[300px] mt-[8px] text-right">
          <input-password
            v-model="password"
            class="!w-full"
            :label="'סיסמה חדשה'"
            :passwordVisibility="passwordVisibility"
            :disabled="isLoading"
            autocomplete="new-password"
          >
            <template v-slot:right_icon>
              <span
                class="material-symbols-rounded text-grays-800 text-[20px] cursor-pointer select-none"
                @click="togglePasswordVisibility"
                >{{ passwordVisibility ? "visibility" : "visibility_off" }}</span
              >
            </template>
          </input-password>
          <div class="flex flex-col gap-[4px] items-start">
            <button
              type="button"
              class="flex items-center gap-[4px] text-sm font-medium text-primary-700 hover:underline disabled:opacity-50"
              :disabled="isLoading"
              @click="suggestPassword"
            >
              <span class="material-symbols-rounded text-base" aria-hidden="true"
                >auto_awesome</span
              >
              הצעת סיסמה תקינה
            </button>
            <p
              v-if="suggestionNotice"
              class="text-xs font-medium"
              :class="suggestionCopied ? 'text-success' : 'text-grays-800'"
              role="status"
            >
              {{ suggestionNotice }}
            </p>
          </div>
          <password-checks
            :password="password"
            :context="checkContext"
            :showMeter="!errorMessage"
            @update:valid="passwordValid = $event"
          />
          <!-- Server refusal (e.g. the phone number): under the checks, in place of the meter. -->
          <error-block v-if="errorMessage" :errorMessage="errorMessage" />
        </div>
      </div>

      <!-- Enabled only once the meter reads "strong" (every rule passes), and off
           again after a server refusal until the customer edits the password. -->
      <div
        class="flex flex-row items-center justify-between gap-[12px] px-4 py-4 md:px-6 border-t border-grays-400"
      >
        <button
          v-if="!enforced"
          type="button"
          class="text-sm font-medium text-grays-800 underline hover:text-grays-900 disabled:opacity-50"
          :disabled="isLoading"
          @click="askToContinue"
        >
          להמשיך בלי סיסמה חדשה
        </button>
        <span v-else></span>
        <button-primary
          type="submit"
          class="disabled:!text-grays-800"
          :disabled="!passwordValid || isLoading || !!errorMessage"
        >
          <span
            v-if="isLoading"
            class="material-symbols-rounded animate-spin text-[20px]"
            aria-hidden="true"
            >progress_activity</span
          >
          {{ isLoading ? "מעדכנים…" : "לעדכון סיסמה" }}
        </button-primary>
      </div>
    </form>

    <div
      v-if="confirmingLater && !enforced"
      class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="continue-warning-title"
    >
      <div
        class="bg-white rounded shadow-modal w-full max-w-[420px] flex flex-col gap-[16px] p-6 md:p-8"
        dir="rtl"
      >
        <h2
          id="continue-warning-title"
          class="text-xl font-bold text-primary-900 text-center"
        >
          שימו לב
        </h2>
        <p class="text-base text-grays-900 text-center">
          החל מ-<bdi class="font-bold">{{ deadlineText }}</bdi> כניסה לאתר תתאפשר עם
          סיסמה חדשה בלבד. מי שלא יעדכן, תיחסם לו הגישה לאתר.
        </p>
        <p class="text-sm text-grays-800 text-center">
          כרגע אפשר להמשיך. נזכיר לכם שוב מחר.
        </p>
        <button-primary
          type="button"
          class="!w-full self-stretch"
          @click="confirmContinue"
        >
          הבנתי, להמשיך
        </button-primary>
        <button
          type="button"
          class="self-center text-sm font-medium text-primary-700 hover:underline"
          @click="backToPassword"
        >
          חזרה להחלפת סיסמה
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from "@/helpers/axios";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { generatePassword, loadZxcvbn } from "@/utils/passwordRules";
import { daysUntil, deadlineShortLabel } from "@/utils/passwordDeadline";

export default {
  name: "force-password-change-modal",
  data() {
    return {
      password: "",
      passwordValid: false,
      passwordVisibility: false,
      isLoading: false,
      errorMessage: "",
      suggestedPassword: "",
      suggestionNotice: "",
      suggestionCopied: false,
      confirmingLater: false,
    };
  },
  computed: {
    ...mapGetters({
      enforced: "forcePasswordChange/isEnforced",
      deadline: "forcePasswordChange/deadline",
    }),
    // "29.10.26 (י״ח בחשוון)"
    deadlineText() {
      return deadlineShortLabel(this.deadline);
    },
    // What the password must not equal; best effort from the stored user.
    checkContext() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return {
          email: user.email,
          username: user.username,
          tzl: user.tzl,
          tzw: user.tzw,
          phones: (user.phones || []).map((p) => p.tel ?? p),
        };
      } catch (e) {
        return {};
      }
    },
  },
  watch: {
    password(value) {
      // A server refusal (e.g. same as the current password) clears once the customer types again.
      this.errorMessage = "";
      // Typing over the suggestion makes the "copied" note stale.
      if (this.suggestedPassword && value !== this.suggestedPassword) {
        this.suggestedPassword = "";
        this.suggestionNotice = "";
      }
    },
    // A 423 after the deadline turns an open reminder into the blocking popup.
    enforced(value, old) {
      if (value && !old) {
        this.track("password_popup_became_blocking");
      }
    },
  },
  mounted() {
    // Escape never reaches the modals underneath and never closes this popup
    // (neither does a click outside it). The only way past it before the
    // deadline is "continue without a new password" and its warning window.
    document.addEventListener("keydown", this.onKeydown, true);
    this.track("password_popup_shown");
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown, true);
  },
  methods: {
    ...mapActions({
      done: "forcePasswordChange/done",
      dismiss: "forcePasswordChange/dismiss",
    }),
    ...mapMutations({ setToken: "user/setToken" }),
    // Google Analytics: every click in the popup. mode = reminder (before the
    // deadline) or blocking (from it); days_to_deadline = 0 on the deadline day.
    // Never send the password or any customer detail.
    track(name, params = {}) {
      try {
        this.$gtag?.event(name, {
          mode: this.enforced ? "blocking" : "reminder",
          days_to_deadline: daysUntil(this.deadline),
          ...params,
        });
      } catch (e) {
        // analytics must never break the popup
      }
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        // Escape only steps back out of the warning window.
        if (this.confirmingLater) {
          this.track("password_popup_warning_back", { via: "escape" });
        }
        this.confirmingLater = false;
      }
    },
    togglePasswordVisibility() {
      this.passwordVisibility = !this.passwordVisibility;
      this.track("password_popup_toggle_visibility", {
        visible: this.passwordVisibility,
      });
    },
    // "Continue without a new password": opens the warning about the deadline.
    askToContinue() {
      if (!this.enforced && !this.isLoading) {
        this.track("password_popup_continue_click");
        this.confirmingLater = true;
      }
    },
    // "Back to changing the password" in the warning window.
    backToPassword() {
      this.track("password_popup_warning_back", { via: "button" });
      this.confirmingLater = false;
    },
    // "Understood, continue": closes the reminder until tomorrow.
    confirmContinue() {
      if (!this.enforced) {
        this.track("password_popup_warning_confirm");
        this.confirmingLater = false;
        this.dismiss();
      }
    },
    // Fills the field with a password that passes every rule, shows it in
    // clear text and copies it so the customer can paste it into a password manager.
    async suggestPassword() {
      this.track("password_popup_suggest_click");
      const zxcvbn = await loadZxcvbn();
      const suggestion = generatePassword(this.checkContext, zxcvbn);
      this.errorMessage = "";
      this.password = suggestion;
      this.suggestedPassword = suggestion;
      this.passwordVisibility = true;
      this.suggestionCopied = await this.copyToClipboard(suggestion);
      this.track("password_popup_suggest_copied", { copied: this.suggestionCopied });
      this.suggestionNotice = this.suggestionCopied
        ? "הסיסמה הועתקה ללוח. אפשר להדביק אותה בכל מקום ולשמור אותה."
        : "לא הצלחנו להעתיק אוטומטית. אפשר לסמן את הסיסמה ולהעתיק אותה ידנית.";
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) {
        // Older browsers / blocked clipboard API: fall back to a temporary textarea.
        try {
          const area = document.createElement("textarea");
          area.value = text;
          area.setAttribute("readonly", "");
          area.style.position = "fixed";
          area.style.opacity = "0";
          document.body.appendChild(area);
          area.select();
          const ok = document.execCommand("copy");
          document.body.removeChild(area);
          return ok;
        } catch (err) {
          return false;
        }
      }
    },
    async submit() {
      if (!this.passwordValid || this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.errorMessage = "";
      this.track("password_popup_submit", {
        used_suggestion: this.password === this.suggestedPassword,
      });
      try {
        const { data } = await axiosInstance.post("/password/force-change", {
          password: this.password,
        });
        localStorage.setItem("token", data.access_token);
        this.setToken(data.access_token);
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.must_change_password = false;
          localStorage.setItem("user", JSON.stringify(user));
        } catch (e) {
          // the next /profile call refreshes it
        }
        try {
          // App.vue shows "הסיסמה עודכנה" once the page is back.
          sessionStorage.setItem("password_updated", "1");
        } catch (e) {
          // no storage: the reload still happens, only the message is skipped
        }
        this.track("password_popup_success", {
          used_suggestion: this.password === this.suggestedPassword,
        });
        this.done();
        // Requests that were refused with 423 while the popup was open ran once
        // and stayed failed; a reload gets every screen its data again. The short
        // wait lets the analytics event leave before the page goes away.
        setTimeout(() => window.location.reload(), 300);
      } catch (e) {
        this.track("password_popup_error", {
          reason: !e.response
            ? "network"
            : e.response.status === 422
            ? "rejected_by_server"
            : "other",
          status: e.response?.status ?? 0,
        });
        const data = e.response?.data;
        this.errorMessage =
          data?.errors?.password?.[0] ||
          data?.message ||
          "משהו השתבש בעדכון הסיסמה. אפשר לנסות שוב.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
